import { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentStatus } from './PaymentStatus';
import { config } from '../config';

// Initialize Stripe with the configured public key
const stripePromise = loadStripe(config.stripePublicKey);

// Price constant for watermark removal
const PRICE = 7.00;

interface PaymentButtonProps {
  onPaymentSuccess: () => void;
  onPaymentExpire: () => void;
  shouldDraw: boolean;
  drawCount: number;
}

interface PaymentFormProps {
  clientSecret: string;
  onSuccess: () => void;
}

const PaymentForm = ({ clientSecret, onSuccess }: PaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      console.log('PaymentForm: Starting payment confirmation');
      const { error: submitError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}?payment_success=true`,
        },
      });

      if (submitError) {
        console.error('PaymentForm: Payment error:', submitError);
        setError(submitError.message || 'An error occurred');
      } else {
        console.log('PaymentForm: Payment successful');
        localStorage.setItem('watermarkRemoved', 'true');
        onSuccess();
      }
    } catch (err) {
      console.error('PaymentForm: Unexpected error:', err);
      setError('An unexpected error occurred');
    }

    setProcessing(false);
  }, [stripe, elements, onSuccess]);

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <PaymentElement options={{
          layout: {
            type: 'tabs',
            defaultCollapsed: false
          }
        }} />
        {error && <div style={{ color: 'red', marginTop: '10px' }}>{error}</div>}
        <button
          type="submit"
          disabled={!stripe || processing}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            opacity: !stripe || processing ? 0.7 : 1
          }}
        >
          {processing ? 'Processing...' : `Pay $${PRICE} to Remove Watermark`}
        </button>
      </form>
    </div>
  );
};

export function PaymentButton({ onPaymentSuccess, onPaymentExpire, shouldDraw, drawCount }: PaymentButtonProps) {
  const [isPaid, setIsPaid] = useState(() => {
    const savedPaid = localStorage.getItem('isPaid');
    const savedExpired = localStorage.getItem('hasExpired');
    console.log('PaymentButton: Initial state check', { savedPaid, savedExpired });
    return savedPaid === 'true' && savedExpired !== 'true';
  });
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [remainingDraws, setRemainingDraws] = useState(() => {
    const savedExpired = localStorage.getItem('hasExpired');
    const savedDraws = localStorage.getItem('remainingDraws');
    console.log('PaymentButton: Initial draws check', { savedExpired, savedDraws });
    if (savedExpired === 'true') return 0;
    return savedDraws ? parseInt(savedDraws) : 5;
  });
  const [hasExpired, setHasExpired] = useState(() => {
    const savedExpired = localStorage.getItem('hasExpired');
    console.log('PaymentButton: Initial expired check', { savedExpired });
    return savedExpired === 'true';
  });
  const [sessionId] = useState(() => {
    const existingSessionId = localStorage.getItem('paymentSessionId');
    if (existingSessionId) {
      console.log('PaymentButton: Using existing session ID', { existingSessionId });
      return existingSessionId;
    }
    const newSessionId = Math.random().toString(36).substring(2, 15);
    console.log('PaymentButton: Created new session ID', { newSessionId });
    localStorage.setItem('paymentSessionId', newSessionId);
    return newSessionId;
  });

  const handlePaymentClick = useCallback(async () => {
    console.log('PaymentButton: Payment button clicked');
    try {
      const response = await fetch(`${config.apiUrl}/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('PaymentButton: Payment intent created', { data });
      
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setShowPaymentForm(true);
      } else {
        console.error('PaymentButton: No client secret received from server');
        throw new Error('No client secret received from server');
      }
    } catch (error) {
      console.error('PaymentButton: Error creating payment:', error);
      alert('Failed to initialize payment. Please try again later.');
    }
  }, [sessionId]);

  // Effect to check payment status
  useEffect(() => {
    if (hasExpired) {
      console.log('PaymentButton: Skipping payment check - expired');
      return;
    }

    const checkPaymentSuccess = async () => {
      console.log('PaymentButton: Checking payment status', { sessionId });
      try {
        const response = await fetch(`${config.apiUrl}/initialize-payment/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('PaymentButton: Payment status response', { data, isPaid });
        
        if (data.success && !isPaid) {
          console.log('PaymentButton: Payment confirmed on server');
          setIsPaid(true);
          setRemainingDraws(5);
          localStorage.setItem('isPaid', 'true');
          localStorage.setItem('remainingDraws', '5');
          onPaymentSuccess();
        }
      } catch (error) {
        console.error('PaymentButton: Error checking payment status:', error);
        setIsPaid(false);
      }
    };

    checkPaymentSuccess();
  }, [sessionId, onPaymentSuccess, isPaid, hasExpired]);

  // Effect to handle draw count and payment expiration
  useEffect(() => {
    console.log('PaymentButton: Draw count effect', { isPaid, drawCount, remainingDraws });
    if (isPaid && drawCount > 0) {
      const newRemaining = 5 - drawCount;
      console.log('PaymentButton: Updating remaining draws', { drawCount, newRemaining });
      setRemainingDraws(newRemaining);
      localStorage.setItem('remainingDraws', newRemaining.toString());
      
      if (newRemaining <= 0) {
        console.log('PaymentButton: Draws expired');
        setIsPaid(false);
        setHasExpired(true);
        localStorage.setItem('isPaid', 'false');
        localStorage.setItem('hasExpired', 'true');
        onPaymentExpire();
      }
    }
  }, [drawCount, isPaid, onPaymentExpire]);

  const handlePaymentSuccess = useCallback(() => {
    console.log('PaymentButton: Payment success handler called');
    setIsPaid(true);
    setRemainingDraws(5);
    setHasExpired(false);
    localStorage.setItem('isPaid', 'true');
    localStorage.setItem('hasExpired', 'false');
    localStorage.setItem('remainingDraws', '5');
    onPaymentSuccess();
  }, [onPaymentSuccess]);

  // Effect to check URL parameters for payment success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    console.log('PaymentButton: Checking URL parameters', { paymentSuccess });
    
    if (paymentSuccess === 'true') {
      console.log('PaymentButton: Payment success detected in URL');
      handlePaymentSuccess();
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [handlePaymentSuccess]);

  return (
    <div>
      {!isPaid && !showPaymentForm && (
        <button
          onClick={handlePaymentClick}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Remove Watermark ($7)
        </button>
      )}

      {showPaymentForm && clientSecret && (
        <Elements 
          stripe={stripePromise} 
          options={{ 
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#007bff',
                colorBackground: '#ffffff',
                colorText: '#30313d',
                colorDanger: '#df1b41',
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                spacingUnit: '4px',
                borderRadius: '4px'
              }
            }
          }}
        >
          <PaymentForm
            clientSecret={clientSecret}
            onSuccess={handlePaymentSuccess}
          />
        </Elements>
      )}

      {isPaid && (
        <div style={{ marginTop: '10px' }}>
          <PaymentStatus
            remainingDraws={remainingDraws}
            hasExpired={hasExpired}
          />
        </div>
      )}
    </div>
  );
} 